/**
 * BETA5 Bespoke Builder \u2014 Guided Entry Flow
 * Chocolatier's Selection: preferences panel, randomise, reveal summary.
 * Reads window.bespokeBuilder and window.debugBespokeBuilder.
 *
 * INVENTORY (changed August 2026):
 * Flavour ceilings account for quantities already committed to bundles sitting
 * in the customer's cart, not just warehouse stock. Without this the
 * randomiser can build a box from stock already spoken for by another box in
 * the same cart, and the conflict only surfaces at checkout.
 */
(function () {
  'use strict';

  var config = window.bespokeBuilder;
  if (!config || !config.variants || !config.chocolates) return;

  var VEGAN_EXCLUDES = ['dairy', 'egg', 'gelatine'];
  var PARTICLE_COLORS = ['#999', '#bbb', '#777', '#ddd', '#555'];

  /* ============================
     DOM REFERENCES
     ============================ */

  var panel, summary, builderMain, prefsPane, loadingPane;
  var sizeContainer, pillBtns, buildBtn, skipBtn, browseCountEl;
  var sizeCards = [];
  var steps;
  var flippedCard = null;

  function cacheDom() {
    panel         = document.getElementById('bb-guided-panel');
    summary       = document.getElementById('bb-guided-summary');
    builderMain   = document.getElementById('bb-builder-main');
    prefsPane     = document.getElementById('bb-guided-prefs');
    loadingPane   = document.getElementById('bb-guided-loading');
    buildBtn      = document.getElementById('bb-guided-build');
    skipBtn       = document.getElementById('bb-guided-skip');
    browseCountEl = document.getElementById('bb-guided-browse-count');
    sizeContainer = document.getElementById('bb-guided-sizes');
    pillBtns      = document.querySelectorAll('.bb-guided-pill');
    steps         = document.querySelectorAll('.bb-guided-step');
  }

  /* ============================
     STATE
     ============================ */

  var selectedVariant = config.variants.find(function (v) {
    return v.size === config.defaultSize;
  }) || config.variants[0];

  var activeFilters = new Set();
  var currentSelection = null;

  /* ============================
     AVAILABILITY
     ============================ */

  /**
   * Effective ceiling for a flavour. Defers to the builder's cart-aware
   * calculation when it is present, so both entry paths use one definition.
   * Falls back to raw stock if the builder has not initialised yet.
   */
  function getAvailable(choc) {
    var api = window.debugBespokeBuilder;
    if (api && typeof api.getAvailableFor === 'function') {
      return api.getAvailableFor(choc);
    }
    if (!choc) return 0;
    if (choc.inventoryPolicy !== 'deny') return Infinity;
    return Math.max(0, choc.inventory || 0);
  }

  /**
   * Re-read what the cart already holds before building, so the randomiser
   * works from current numbers rather than whatever was true on page load.
   */
  function refreshCommitments() {
    var api = window.debugBespokeBuilder;
    if (api && typeof api.refreshCartCommitments === 'function') {
      return api.refreshCartCommitments();
    }
    return Promise.resolve();
  }

  /* ============================
     SIZE SELECTOR
     ============================ */

  var isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  function initSizes() {
    if (!sizeContainer) return;

    config.variants.forEach(function (v) {
      if (v.size === 0) return;

      var card = document.createElement('div');
      card.className = 'bb-guided-size-card' + (v.id === selectedVariant.id ? ' bb-guided-size-card--active' : '');
      card.dataset.variantId = v.id;
      card.dataset.size = v.size;

      // Front = interior (open box), Back = exterior (closed packaging)
      var intSrc = v.imageInterior || '';
      var extSrc = v.imageExterior || '';

      card.innerHTML =
        '<div class="bb-guided-size-flip">' +
          '<div class="bb-guided-size-flip__front">' +
            (intSrc ? '<img src="' + intSrc + '" alt="' + v.size + 'pc box open" width="300" height="300" loading="lazy">' : '') +
          '</div>' +
          '<div class="bb-guided-size-flip__back">' +
            (extSrc ? '<img src="' + extSrc + '" alt="' + v.size + 'pc box closed" width="300" height="300" loading="lazy">' : '') +
          '</div>' +
        '</div>' +
        '<div class="bb-guided-size-label">' +
          '<span class="bb-guided-size-label__size">' + v.size + 'pc</span>' +
          '<span class="bb-guided-size-label__price">' + v.priceFormatted + '</span>' +
        '</div>';

      card.addEventListener('click', function () {
        selectSizeCard(v);
      });

      if (isTouchDevice) {
        card.addEventListener('touchstart', function () {
          if (flippedCard === card) {
            card.classList.remove('flipped');
            flippedCard = null;
            return;
          }
          if (flippedCard) flippedCard.classList.remove('flipped');
          card.classList.add('flipped');
          flippedCard = card;
        }, { passive: true });
      }

      sizeContainer.appendChild(card);
      sizeCards.push(card);
    });
  }

  function selectSizeCard(v) {
    selectedVariant = v;
    sizeCards.forEach(function (c) {
      c.classList.toggle('bb-guided-size-card--active',
        parseInt(c.dataset.variantId, 10) === v.id);
    });
  }

  /* ============================
     FILTER PILLS
     ============================ */

  function initPills() {
    pillBtns.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var allergen = pill.dataset.allergen;
        if (activeFilters.has(allergen)) {
          activeFilters.delete(allergen);
          pill.classList.remove('active');
        } else {
          activeFilters.add(allergen);
          pill.classList.add('active');
        }
        updateBrowseCount();
      });
    });
  }

  /* ============================
     DYNAMIC BROWSE COUNT
     ============================ */

  function countAvailableForFilters(filters) {
    return config.chocolates.filter(function (choc) {
      if (!choc.available) return false;
      if (filters.size === 0) return true;
      var dominated = false;
      filters.forEach(function (filter) {
        if (filter === 'vegan') {
          if (VEGAN_EXCLUDES.some(function (a) { return (choc.allergens || []).indexOf(a) !== -1; })) dominated = true;
        } else {
          if ((choc.allergens || []).indexOf(filter) !== -1) dominated = true;
        }
      });
      return !dominated;
    }).length;
  }

  function updateBrowseCount() {
    if (!browseCountEl) return;
    var count = countAvailableForFilters(activeFilters);
    if (activeFilters.size === 0) {
      browseCountEl.textContent = 'Browse all ' + count + ' flavours.';
    } else {
      browseCountEl.textContent = 'Browse ' + count + ' flavours matching your preferences.';
    }
  }

  /* ============================
     STEP INDICATOR
     ============================ */

  function setStep(num) {
    steps.forEach(function (s) {
      var n = parseInt(s.dataset.step, 10);
      s.classList.toggle('bb-guided-step--active', n === num);
      s.classList.toggle('bb-guided-step--done', n < num);
    });
  }

  /* ============================
     FILTER LOGIC (mirrors v3)
     ============================ */

  function passesFilters(choc) {
    if (activeFilters.size === 0) return true;
    var allergens = choc.allergens || [];
    var dominated = false;
    activeFilters.forEach(function (filter) {
      if (filter === 'vegan') {
        if (VEGAN_EXCLUDES.some(function (a) { return allergens.indexOf(a) !== -1; })) dominated = true;
      } else {
        if (allergens.indexOf(filter) !== -1) dominated = true;
      }
    });
    return !dominated;
  }

  /* ============================
     RANDOMISE ALGORITHM
     ============================ */

  function shuffleArray(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i]; arr[i] = arr[j]; arr[j] = tmp;
    }
    return arr;
  }

  function randomizeBox(size) {
    var pool = config.chocolates.filter(function (c) { return c.available; });
    pool = pool.filter(passesFilters);
    if (pool.length === 0) return { selection: {}, filled: 0, requested: size };

    shuffleArray(pool);

    var selection = {};
    var filled = 0;

    // Round 1: variety first, one of each
    for (var i = 0; i < pool.length && filled < size; i++) {
      var c = pool[i];
      var pid = String(c.id);
      if (getAvailable(c) < 1) continue;
      selection[pid] = 1;
      filled++;
    }

    // Round 2+: duplicate within the cart-aware ceiling
    if (filled < size) {
      var passes = 0;
      while (filled < size && passes < 100) {
        passes++;
        var progressed = false;
        for (var j = 0; j < pool.length && filled < size; j++) {
          var c2 = pool[j];
          var pid2 = String(c2.id);
          var current = selection[pid2] || 0;
          if (current < getAvailable(c2)) {
            selection[pid2] = current + 1;
            filled++;
            progressed = true;
          }
        }
        // Every flavour is at its ceiling: no further passes will help
        if (!progressed) break;
      }
    }

    return { selection: selection, filled: filled, requested: size };
  }

  /* ============================
     PARTIAL RE-ROLL ALGORITHM
     ============================ */

  function rerollMarked(fullSelection, markedPids) {
    var slotsToFill = 0;
    markedPids.forEach(function (pid) {
      slotsToFill += (fullSelection[pid] || 0);
    });

    // Build kept selection (unmarked cards)
    var kept = {};
    Object.keys(fullSelection).forEach(function (pid) {
      if (markedPids.indexOf(pid) === -1) {
        kept[pid] = fullSelection[pid];
      }
    });

    // Build pool: available + passes filters
    var pool = config.chocolates.filter(function (c) { return c.available; });
    pool = pool.filter(passesFilters);

    // Exclude both kept pids (for variety) AND marked pids (customer rejected)
    var excludeSet = {};
    Object.keys(kept).forEach(function (pid) { excludeSet[pid] = true; });
    markedPids.forEach(function (pid) { excludeSet[pid] = true; });

    var rerollPool = pool.filter(function (c) { return !excludeSet[String(c.id)]; });

    if (rerollPool.length === 0) return null;

    shuffleArray(rerollPool);

    var newPicks = {};
    var filled = 0;

    // Round 1: one of each, variety-first
    for (var i = 0; i < rerollPool.length && filled < slotsToFill; i++) {
      var c = rerollPool[i];
      var pid = String(c.id);
      var totalForPid = (kept[pid] || 0) + (newPicks[pid] || 0);
      if (totalForPid < getAvailable(c)) {
        newPicks[pid] = (newPicks[pid] || 0) + 1;
        filled++;
      }
    }

    // Round 2+: allow repeats within rerollPool (never re-introduce markedPids)
    if (filled < slotsToFill) {
      var passes = 0;
      while (filled < slotsToFill && passes < 100) {
        passes++;
        var progressed = false;
        for (var j = 0; j < rerollPool.length && filled < slotsToFill; j++) {
          var c2 = rerollPool[j];
          var pid2 = String(c2.id);
          var total2 = (kept[pid2] || 0) + (newPicks[pid2] || 0);
          if (total2 < getAvailable(c2)) {
            newPicks[pid2] = (newPicks[pid2] || 0) + 1;
            filled++;
            progressed = true;
          }
        }
        if (!progressed) break;
      }
    }

    // A short re-roll would silently shrink the box. Refuse instead.
    if (filled < slotsToFill) return null;

    // Merge kept + new picks
    var merged = {};
    Object.keys(kept).forEach(function (pid) { merged[pid] = kept[pid]; });
    Object.keys(newPicks).forEach(function (pid) {
      merged[pid] = (merged[pid] || 0) + newPicks[pid];
    });

    return { selection: merged, swappedPids: Object.keys(newPicks) };
  }

  /* ============================
     SYNC FILTERS TO BUILDER
     ============================ */

  function syncFiltersToBuilder() {
    var api = window.debugBespokeBuilder;
    if (!api) return;
    api.clearFilters();
    activeFilters.forEach(function (f) { api.addFilter(f); });
  }

  /* ============================
     SHOW TOAST
     ============================ */

  function showToast(msg, duration) {
    var toast = document.getElementById('bb-toast');
    if (!toast) { alert(msg); return; }
    toast.textContent = msg;
    toast.classList.add('visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () { toast.classList.remove('visible'); }, duration || 4000);
  }

  /* ============================
     PARTICLE BURST ANIMATION
     ============================ */

  function emitParticles(cardEl) {
    var imgWrap = cardEl.querySelector('.bb-summary-card__img-wrap');
    if (!imgWrap) return;

    var container = document.createElement('div');
    container.className = 'bb-summary-card__particles';
    imgWrap.appendChild(container);

    for (var i = 0; i < 10; i++) {
      var angle = (Math.PI * 2 / 10) * i + (Math.random() * 0.4 - 0.2);
      var radius = 30 + Math.random() * 20;
      var tx = Math.cos(angle) * radius;
      var ty = Math.sin(angle) * radius;

      var particle = document.createElement('span');
      particle.className = 'bb-particle';
      particle.style.background = PARTICLE_COLORS[i % PARTICLE_COLORS.length];
      particle.style.setProperty('--tx', tx + 'px');
      particle.style.setProperty('--ty', ty + 'px');
      container.appendChild(particle);
    }

    // Cleanup after animation
    setTimeout(function () {
      if (container.parentNode) container.parentNode.removeChild(container);
    }, 600);
  }

  function animateCardSwap(cardEl, newChoc, newQty) {
    var imgWrap = cardEl.querySelector('.bb-summary-card__img-wrap');
    var img = cardEl.querySelector('.bb-guided-grid-img');
    var label = cardEl.querySelector('.bb-summary-card__label');
    var qtyEl = cardEl.querySelector('.bb-summary-card__qty');

    // 1. Emit particles + fade out
    emitParticles(cardEl);
    if (imgWrap) imgWrap.style.animation = 'bb-card-fade-out 0.2s ease forwards';

    // 2. Swap content at 500ms
    setTimeout(function () {
      if (img) {
        img.src = newChoc.imageLarge || newChoc.image || '';
        img.alt = newChoc.title;
      }
      if (label) label.textContent = newChoc.title;
      if (qtyEl) qtyEl.textContent = newQty > 1 ? '\u00D7' + newQty : '';
      if (qtyEl) qtyEl.style.display = newQty > 1 ? '' : 'none';
      cardEl.dataset.pid = String(newChoc.id);

      // 3. Fade in
      if (imgWrap) imgWrap.style.animation = 'bb-card-fade-in 0.3s ease forwards';
    }, 500);

    // 4. Clear animation style
    setTimeout(function () {
      if (imgWrap) imgWrap.style.animation = '';
    }, 850);
  }

  /* ============================
     REVEAL SUMMARY
     ============================ */

  function renderSummary(selection) {
    currentSelection = selection;
    var totalPieces = 0;
    Object.values(selection).forEach(function (q) { totalPieces += q; });

    var colMap = { 4: 4, 6: 3, 12: 4, 24: 6 };
    var cols = colMap[selectedVariant.size] || 4;

    var html = '';
    html += '<div class="bb-guided-steps">';
    html += '<span class="bb-guided-step bb-guided-step--done" data-step="1">Preferences</span>';
    html += '<span class="bb-guided-step-sep">\u203A</span>';
    html += '<span class="bb-guided-step bb-guided-step--active" data-step="2">Your selection</span>';
    html += '<span class="bb-guided-step-sep">\u203A</span>';
    html += '<span class="bb-guided-step" data-step="3">Cart</span>';
    html += '</div>';

    html += '<div class="bb-guided-summary-head">';
    html += '<h2 class="bb-guided-title">And the chocolatiers have spoken.</h2>';
    html += '<p class="bb-guided-sub">' + totalPieces + ' pieces, handpicked for you. Tap any flavour to swap it out.</p>';
    html += '</div>';

    html += '<div class="bb-guided-grid" style="grid-template-columns: repeat(' + cols + ', 100px);">';
    Object.entries(selection).forEach(function (entry) {
      var pid = entry[0];
      var qty = entry[1];
      var choc = config.chocolates.find(function (c) { return String(c.id) === pid; });
      if (!choc) return;
      html += '<div class="bb-summary-card" data-pid="' + pid + '">';
      html += '<div class="bb-summary-card__img-wrap">';
      html += '<img class="bb-guided-grid-img" src="' + (choc.imageLarge || choc.image || '') + '" alt="' + choc.title + '" width="120" height="120">';
      if (qty > 1) {
        html += '<span class="bb-guided-grid-qty">\u00D7' + qty + '</span>';
      }
      html += '<div class="bb-summary-card__overlay"><span class="bb-summary-card__swap-icon">\u21BA</span></div>';
      html += '</div>';
      html += '<span class="bb-summary-card__label bb-guided-grid-label">' + choc.title + '</span>';
      if (qty > 1) {
        html += '<span class="bb-summary-card__qty bb-guided-grid-qty" style="position:static;">\u00D7' + qty + '</span>';
      } else {
        html += '<span class="bb-summary-card__qty" style="display:none;"></span>';
      }
      html += '</div>';
    });
    html += '</div>';

    html += '<div class="bb-guided-cta-row bb-guided-cta-row--summary">';
    html += '<button class="bb-guided-btn-primary" id="bb-guided-accept" type="button">Add to cart</button>';
    html += '<button class="bb-guided-btn-secondary bb-summary-reroll-btn" id="bb-guided-reroll" type="button" style="display:none">';
    html += '<span class="bb-guided-btn-label">Work your magic</span>';
    html += '<span class="bb-guided-btn-desc">We\u2019ll replace your picks with fresh surprises.</span>';
    html += '</button>';
    html += '<button class="bb-guided-btn-secondary bb-summary-offramp-btn" id="bb-guided-swap" type="button">';
    html += '<span class="bb-guided-btn-label">I\u2019ll take it from here</span>';
    html += '<span class="bb-guided-btn-desc">Jump into the full menu and adjust as you like.</span>';
    html += '</button>';
    html += '</div>';

    summary.innerHTML = html;

    // Wire card marking
    summary.querySelectorAll('.bb-summary-card').forEach(function (card) {
      card.addEventListener('click', function () {
        card.classList.toggle('bb-summary-card--marked');
        updateRerollVisibility();
      });
    });

    // Wire CTAs
    document.getElementById('bb-guided-accept').addEventListener('click', function () {
      acceptAndSubmit(currentSelection);
    });
    document.getElementById('bb-guided-reroll').addEventListener('click', handleReroll);
    document.getElementById('bb-guided-swap').addEventListener('click', function () {
      applyAndShowBuilder(currentSelection);
    });
  }

  function updateRerollVisibility() {
    var marked = summary.querySelectorAll('.bb-summary-card--marked');
    var rerollBtn = document.getElementById('bb-guided-reroll');
    if (rerollBtn) {
      rerollBtn.style.display = marked.length > 0 ? '' : 'none';
    }
  }

  function handleReroll() {
    var markedCards = summary.querySelectorAll('.bb-summary-card--marked');
    var markedPids = [];
    markedCards.forEach(function (card) { markedPids.push(card.dataset.pid); });
    if (markedPids.length === 0) return;

    var result = rerollMarked(currentSelection, markedPids);
    if (!result) {
      showToast('Not enough left to swap those out \u2014 try marking fewer, or adjusting your preferences.', 5000);
      return;
    }

    // Animate swapped cards, then update state
    var swappedSet = {};
    result.swappedPids.forEach(function (pid) { swappedSet[pid] = true; });

    // Start particle animations on marked cards
    markedCards.forEach(function (cardEl) {
      emitParticles(cardEl);
      var imgWrap = cardEl.querySelector('.bb-summary-card__img-wrap');
      if (imgWrap) imgWrap.style.animation = 'bb-card-fade-out 0.2s ease forwards';
    });

    // After fade-out + particles, re-render with new selection
    setTimeout(function () {
      currentSelection = result.selection;
      renderSummary(result.selection);

      // Add fade-in animation to swapped cards
      summary.querySelectorAll('.bb-summary-card').forEach(function (card) {
        if (swappedSet[card.dataset.pid]) {
          var imgWrap = card.querySelector('.bb-summary-card__img-wrap');
          if (imgWrap) {
            imgWrap.style.animation = 'bb-card-fade-in 0.3s ease forwards';
            setTimeout(function () { imgWrap.style.animation = ''; }, 350);
          }
        }
      });

      summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  }

  /* ============================
     SUBMIT / SHOW BUILDER
     ============================ */

  function acceptAndSubmit(selection) {
    var api = window.debugBespokeBuilder;
    if (!api) return;

    api.setSize(selectedVariant.id);
    syncFiltersToBuilder();
    api.applyRandomSelection(selection);
    api.handleSubmit();
  }

  function applyAndShowBuilder(selection) {
    var api = window.debugBespokeBuilder;
    if (!api) return;

    api.setSize(selectedVariant.id);
    syncFiltersToBuilder();
    api.applyRandomSelection(selection);

    panel.style.display = 'none';
    summary.style.display = 'none';
    builderMain.style.display = '';
    builderMain.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ============================
     FLOW HANDLERS
     ============================ */

  function handleBuildMyBox() {
    prefsPane.style.display = 'none';
    loadingPane.style.display = '';

    // Read current stock and cart commitments before building, so the
    // randomiser cannot pick flavours already spoken for by another box.
    var ready = Promise.resolve();
    var api = window.debugBespokeBuilder;
    if (api && typeof api.refreshInventory === 'function') {
      ready = Promise.resolve(api.refreshInventory());
    }
    ready = ready.then(refreshCommitments).catch(function () {});

    var settled = new Promise(function (resolve) { setTimeout(resolve, 1800); });

    Promise.all([ready, settled]).then(function () {
      var size = selectedVariant.size;
      var result = randomizeBox(size);

      if (!result || result.filled === 0) {
        loadingPane.style.display = 'none';
        prefsPane.style.display = '';
        showToast('No flavours match your current preferences. Try removing a filter.', 5000);
        return;
      }

      // A short box would silently sell fewer pieces than the customer paid
      // for, and would fail validation at submit with no explanation.
      if (result.filled < size) {
        loadingPane.style.display = 'none';
        prefsPane.style.display = '';
        showToast(
          'We could only fill ' + result.filled + ' of the ' + size +
          ' pieces right now. Try a smaller box, removing a filter, or checking what is already in your cart.',
          6000
        );
        return;
      }

      loadingPane.style.display = 'none';
      panel.style.display = 'none';
      setStep(2);
      renderSummary(result.selection);
      summary.style.display = '';
      summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function handleSkip() {
    var api = window.debugBespokeBuilder;
    if (!api) return;

    api.setSize(selectedVariant.id);
    syncFiltersToBuilder();

    panel.style.display = 'none';
    summary.style.display = 'none';
    builderMain.style.display = '';
    builderMain.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ============================
     INIT
     ============================ */

  function init() {
    cacheDom();
    if (!panel || !builderMain) return;

    initSizes();
    initPills();
    setStep(1);
    updateBrowseCount();

    buildBtn.addEventListener('click', handleBuildMyBox);
    skipBtn.addEventListener('click', handleSkip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();